import React, { useCallback, useEffect, useState } from 'react';
import { App, Button } from 'antd';

import RecordBadge from '../common/RecordBadge';
import TabLoadState from '../common/TabLoadState';
import ToggleRow from '../common/ToggleRow';
import { usePreferences } from '../../contexts/PreferencesContext';
import { followRefusal, listItemFollows, unfollowItem } from '../../services/itemFollows';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { apiErrorMessage } from '../../utils/apiErrors';

const PAGE = 50;

const itemPath = row => `/collections/${row.collection?._id}/items/${row.item_id}`;

/**
 * Settings › Notifications › **Items you follow** (keepr-api docs/SCHEMA.md §
 * Following an item; KPR-391): every item the person follows, newest first,
 * each with Unfollow, and the one switch that decides whether their changes
 * are mailed as well as put in the inbox (`itemFollowEmails`, its own
 * preference — someone following a dozen town reports may want those in the
 * inbox only and their alerts mailed).
 *
 * A row the person can no longer read — access taken away, the item turned
 * private or deleted — comes back from the API as a bare id, and reads here
 * as "An item you can no longer open" with Unfollow beside it. Nothing more:
 * the API sends nothing more (which of those happened IS the disclosure), and
 * the follow tells them nothing until they can read it again.
 *
 * The list pages by "Show more", fifty at a time (the cap is 500 a person).
 * Unfollow takes the row out at once and puts it back if the API says no.
 */
export default function FollowingList() {
	const { message } = App.useApp();
	const { prefs, setPref, loading: prefsLoading } = usePreferences();
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	const [more, setMore] = useState(false);
	const [loaded, setLoaded] = useState(false);

	const load = useCallback(async (skip = 0) => {
		const page = await listItemFollows({ skip, limit: PAGE });
		setRows(current => (skip ? [...current, ...page.rows] : page.rows));
		setTotal(page.total);
	}, []);

	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		let active = true;
		setLoading(true);
		listItemFollows({ skip: 0, limit: PAGE })
			.then((page) => {
				if (!active) return;
				setRows(page.rows);
				setTotal(page.total);
				setLoadError(null);
				setLoaded(true);
			})
			.catch((error) => { if (active) setLoadError(error); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [attempt]);

	const showMore = async () => {
		setMore(true);
		try {
			await load(rows.length);
		} catch (error) {
			message.error(apiErrorMessage(error, 'More could not be loaded. Please try again.'));
		} finally {
			setMore(false);
		}
	};

	const unfollow = async (row) => {
		const before = rows;
		setRows(current => current.filter(r => r.item_id !== row.item_id));
		setTotal(n => Math.max(0, n - 1));
		try {
			await unfollowItem(row.item_id);
		} catch (error) {
			setRows(before);
			setTotal(n => n + 1);
			message.error(followRefusal(error));
		}
	};

	return (
		<section className="form-panel following-list" aria-labelledby="following-list-title">
			<div id="following-list-title" className="micro-label form-panel__kicker">Items you follow</div>
			<p className="form-panel__hint">
				You hear in your inbox when a followed item changes — its status, or whatever its card marks to tell followers. Emails are a short note, at most one per item every 15 minutes.
				Follow an item from its ⋯ menu.
			</p>

			<ToggleRow
				first
				label="Email me when they change"
				description="Off: changes to items you follow reach your inbox only. Turning keepr's email off above stops these too."
				checked={prefs.itemFollowEmails !== false}
				disabled={prefsLoading}
				onChange={value => setPref('itemFollowEmails', value)}
			/>

			{!loaded ? (
				<TabLoadState
					loading={loading}
					error={loadError}
					label="what you follow"
					errorDetail={loadError ? apiErrorMessage(loadError, 'Check your connection and try again.') : undefined}
					onRetry={() => setAttempt(n => n + 1)}
					rows={2}
				/>
			) : null}
			{loaded && !rows.length ? (
				<p className="form-panel__hint following-list__empty">You don't follow any items. Open an item and choose Follow in its ⋯ menu.</p>
			) : null}

			{rows.length ? (
				<ul className="following-list__rows">
					{rows.map(row => (
						<li key={row.item_id} className="following-list__row">
							<div className="following-list__identity">
								{row.available ? (
									<RecordBadge
										kind="item"
										record={row.card || {}}
										kicker={row.card?.name || null}
										title={row.title || 'Untitled'}
										collection={row.collection || undefined}
										to={row.collection?._id ? itemPath(row) : undefined}
										size="sm"
									/>
								) : (
									<span className="following-list__gone">An item you can no longer open</span>
								)}
								<span className="following-list__meta">
									{row.available && row.collection?.name ? `${row.collection.name} · ` : ''}
									Following since {formatDisplayDate(row.followedAt)}
								</span>
							</div>
							<Button
								size="small"
								onClick={() => unfollow(row)}
								aria-label={`Unfollow ${row.available ? (row.title || 'this item') : 'this item'}`}
							>
								Unfollow
							</Button>
						</li>
					))}
				</ul>
			) : null}

			{rows.length < total ? (
				<Button className="following-list__more" onClick={showMore} loading={more}>
					Show more ({total - rows.length})
				</Button>
			) : null}
		</section>
	);
}
